"use client";

import {
  getDestinations as getDefaultDestinations,
  type Destination,
} from "@/app/utils/destinations/destinationData";
import {
  getCountryCodeFromLocalStorage,
  setCountryCodeToLocalStorage,
} from "@/app/utils/services/service/localStorageService";
import { getDestinations } from "@/app/utils/destinations/countryToRegion";
import { getRegionalDestinations } from "@/app/utils/apis/apiService";
import SearchFlightsHero from "@/app/[locale]/(public)/flights/SearchFlightsHero/SearchFlightsHero";
import { Fragment, useEffect, useState } from "react";
import OfferSection from "./OfferSection/OfferSection";
import PlaceSection from "./PlaceSection/PlaceSection";
import BannerSection from "@/app/[locale]/(public)/flights/BannerSection";
import { useLocale, useTranslations } from "next-intl";
import Events from "./Events";
import type { EventType } from "@/app/types/event";
import { useTenantBranding } from "@/app/context/TenantBrandingContext";
import type { TravelFeatureCard } from "@/app/components/common/TravelFeatureCards/TravelFeatureCards";
import { getDefaultTravelFeatureIcon } from "@/app/components/common/TravelFeatureCards/defaultIcons";
import {
  getLocalizedText,
  getTravelFeaturePalette,
  resolveTravelFeatures,
} from "@/app/utils/tenant/travelFeatures";

export default function LandingPage({
  events,
  isSpecialBannerEnabled,
  showEvents,
}: {
  events: EventType[];
  isSpecialBannerEnabled: boolean;
  showEvents?: boolean;
}) {
  const locale = useLocale();
  const t = useTranslations();
  const { banner, travelExperience } = useTenantBranding();

  const bannerTitle = getLocalizedText(banner?.title, locale);
  const bannerDescription = getLocalizedText(banner?.description, locale);
  /* eslint-disable camelcase */
  const staticContentData = {
    ...(banner?.imageUrl ? { banner_image: banner.imageUrl } : {}),
    ...(bannerTitle ? { banner_heading: bannerTitle } : {}),
    ...(bannerDescription ? { banner_description: bannerDescription } : {}),
  };
  /* eslint-enable camelcase */
  const [destination, setDestination] = useState<Destination | null>(null);
  const [destinations, setDestinations] = useState<Destination[] | null>(
    getDefaultDestinations(t)
  );

  const fetchDestinations = async () => {
    const countryCode = getCountryCodeFromLocalStorage();
    if (countryCode) {
      const regionDestinations = getDestinations(t, countryCode);
      setDestinations(regionDestinations);
    } else {
      const data = await getRegionalDestinations(locale);
      setDestinations(data.destinations);
      setCountryCodeToLocalStorage(data.countryCode);
    }
  };

  useEffect(() => {
    fetchDestinations();
  }, []);

  const tenantFeatures = travelExperience?.features ?? [];

  const defaultFeatures: TravelFeatureCard[] = [
    {
      key: "default-1",
      title: t("HomePageHotels.enhanceTravelExperienceList.1.title"),
      description: t(
        "HomePageHotels.enhanceTravelExperienceList.1.description"
      ),
      ...getTravelFeaturePalette(0),
      iconNode: getDefaultTravelFeatureIcon(0),
    },
    {
      key: "default-2",
      title: t("HomePageHotels.enhanceTravelExperienceList.2.title"),
      description: t(
        "HomePageHotels.enhanceTravelExperienceList.2.description"
      ),
      ...getTravelFeaturePalette(1),
      iconNode: getDefaultTravelFeatureIcon(1),
    },
    {
      key: "default-3",
      title: t("HomePageHotels.enhanceTravelExperienceList.3.title"),
      description: t(
        "HomePageHotels.enhanceTravelExperienceList.3.description"
      ),
      ...getTravelFeaturePalette(2),
      iconNode: getDefaultTravelFeatureIcon(2),
    },
    {
      key: "default-4",
      title: t("HomePageHotels.enhanceTravelExperienceList.4.title"),
      description: t(
        "HomePageHotels.enhanceTravelExperienceList.4.description"
      ),
      ...getTravelFeaturePalette(3),
      iconNode: getDefaultTravelFeatureIcon(3),
    },
  ];

  // A tenant that has saved no cards keeps the platform wording.
  const features: TravelFeatureCard[] =
    tenantFeatures.length > 0
      ? resolveTravelFeatures(tenantFeatures, locale).map((feature, index) => ({
          ...feature,
          iconNode: getDefaultTravelFeatureIcon(index),
        }))
      : defaultFeatures;

  return (
    <Fragment>
      <SearchFlightsHero
        // @ts-ignore — Destination | null vs Destination mismatch pre-dates this change
        setDestination={setDestination}
        destination={destination}
        type="hotels"
        staticContentData={staticContentData}
      />
      {showEvents !== false && <Events data={events} type="hotels" />}
      {isSpecialBannerEnabled && <BannerSection />}
      <OfferSection
        features={features}
        heading={getLocalizedText(travelExperience?.heading, locale)}
      />
      <PlaceSection
        setDestination={setDestination}
        destinations={destinations!}
      />
    </Fragment>
  );
}
